import { useEffect, useState } from 'react';
import { api, fmt, LIVE_PLATFORMS, type LivePlatform } from '../api';

const PLATFORMS = LIVE_PLATFORMS;

export default function CreativeStudio() {
  const [products, setProducts] = useState<any[]>([]);
  const [productId, setProductId] = useState<string>('');
  const [platform, setPlatform] = useState<LivePlatform>('meta');
  const [hooks, setHooks] = useState<any[]>([]);
  const [creatives, setCreatives] = useState<any[]>([]);
  const [winners, setWinners] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.products().then((rows) => {
      setProducts(rows);
      if (rows.length) setProductId(rows[0].id);
    });
    api.hookWinners().then(setWinners);
  }, []);

  async function generate() {
    if (!productId) return;
    setLoading(true);
    try {
      const r = await api.generateCreatives({ productId, platform, count: 6 });
      setHooks(r.hooks);
      setCreatives(r.creatives);
    } finally {
      setLoading(false);
    }
  }

  const product = products.find((p) => p.id === productId);

  return (
    <>
      <h2 className="page-title">Creative Studio</h2>
      <p className="page-sub">Generate hooks and creatives. Variants are stored automatically.</p>

      <div className="card card-padded" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 2, marginBottom: 0 }}>
            <label>Product</label>
            <select value={productId} onChange={(e) => setProductId(e.target.value)}>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label>Platform</label>
            <select value={platform} onChange={(e) => setPlatform(e.target.value as any)}>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={generate} disabled={loading}>
            {loading ? 'Generating…' : 'Regenerate 6 variants'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <div>
          <div className="card card-padded">
            <div className="card-title">Generated creatives</div>
            {creatives.length === 0 ? (
              <div className="empty">Click "Regenerate" to produce 6 variants for {platform}.</div>
            ) : (
              creatives.map((c) => (
                <div key={c.id} style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                  {product?.image_url && (
                    <div
                      style={{
                        width: 90,
                        height: 90,
                        flexShrink: 0,
                        borderRadius: 8,
                        background: `url(${product.image_url}) center/cover`,
                      }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{c.headline}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-sub)', marginBottom: 6, whiteSpace: 'pre-line' }}>
                      {c.primaryText}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <span className={`badge badge-${c.platform}`}>{c.platform}</span>
                      <span className="badge badge-draft">{c.format}</span>
                      <span className="badge badge-active">CTA: {c.cta}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="card card-padded" style={{ marginBottom: 16 }}>
            <div className="card-title">
              Global winning hooks
              <span className="muted">moat</span>
            </div>
            {winners.map((h) => (
              <div key={h.id} className="hook-card">
                <div className="text">{h.text}</div>
                <div className="meta">
                  <span><b>{h.emotion}</b></span>
                  <span>ROAS <b>{h.global_roas?.toFixed(2)}×</b></span>
                  <span>CTR <b>{fmt.pct(h.global_ctr ?? 0, 2)}</b></span>
                  <span>{h.uses_count} uses</span>
                </div>
              </div>
            ))}
          </div>

          {hooks.length > 0 && (
            <div className="card card-padded">
              <div className="card-title">Newly generated hooks (ranked)</div>
              {hooks.map((h) => (
                <div key={h.id} className="hook-card">
                  <div className="text">{h.text}</div>
                  <div className="meta">
                    <span><b>{h.emotion}</b></span>
                    <span>template: {h.templateKey}</span>
                    <span>predicted CTR <b>{fmt.pct(h.predictedCtr, 2)}</b></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
